/**
 * Spacing system for Turn Connect applications.
 * Consistent spacing values based on 4px grid system.
 */

export const spacing = {
  // Base spacing unit (4px)
  unit: 4,

  // Core spacing scale (4px grid)
  px: 1,
  0: 0,
  1: 4, // 1 unit
  2: 8, // 2 units
  3: 12, // 3 units
  4: 16, // 4 units
  5: 20, // 5 units
  6: 24, // 6 units
  7: 28, // 7 units
  8: 32, // 8 units
  9: 36, // 9 units
  10: 40, // 10 units
  11: 44, // 11 units
  12: 48, // 12 units
  14: 56, // 14 units
  16: 64, // 16 units
  18: 72, // 18 units
  20: 80, // 20 units
  24: 96, // 24 units
  28: 112, // 28 units
  32: 128, // 32 units
  36: 144, // 36 units
  40: 160, // 40 units
  44: 176, // 44 units
  48: 192, // 48 units
  52: 208, // 52 units
  56: 224, // 56 units
  60: 240, // 60 units
  64: 256, // 64 units
  72: 288, // 72 units
  80: 320, // 80 units
  96: 384, // 96 units

  // Semantic spacing - Component-specific
  component: {
    // Button spacing
    button: {
      paddingX: {
        small: 12,
        medium: 16,
        large: 24,
      },
      paddingY: {
        small: 6,
        medium: 8,
        large: 12,
      },
      gap: 8,
    },

    // Form spacing
    form: {
      fieldGap: 16,
      sectionGap: 24,
      labelGap: 4,
      helpTextGap: 4,
      groupGap: 12,
    },

    // Card spacing
    card: {
      padding: {
        small: 12,
        medium: 16,
        large: 24,
      },
      gap: 12,
      margin: 16,
    },

    // Modal spacing
    modal: {
      padding: 16,
      headerGap: 16,
      bodyGap: 16,
      footerGap: 12,
      margin: 16,
    },

    // List spacing
    list: {
      itemGap: 8,
      itemPadding: 12,
      sectionGap: 16,
      indent: 16,
    },

    // Navigation spacing
    navigation: {
      itemGap: 4,
      itemPadding: 12,
      sectionGap: 24,
      breadcrumbGap: 8,
    },

    // Table spacing
    table: {
      cellPadding: {
        x: 12,
        y: 8,
      },
      headerPadding: {
        x: 12,
        y: 12,
      },
      rowGap: 0,
    },
  },

  // Layout spacing - Page structure
  layout: {
    // Container spacing
    container: {
      paddingX: {
        mobile: 16,
        tablet: 24,
        desktop: 32,
      },
      maxWidth: 1200,
    },

    // Section spacing
    section: {
      paddingY: {
        small: 24,
        medium: 48,
        large: 72,
      },
      gap: 32,
    },

    // Grid spacing
    grid: {
      gap: {
        small: 12,
        medium: 16,
        large: 24,
      },
      columnGap: 24,
      rowGap: 16,
    },

    // Sidebar spacing
    sidebar: {
      width: 240,
      padding: 16,
      itemGap: 8,
    },

    // Header spacing
    header: {
      height: 64,
      padding: 16,
      itemGap: 12,
    },

    // Footer spacing
    footer: {
      padding: 24,
      sectionGap: 32,
      itemGap: 16,
    },
  },

  // Content spacing - Text and media
  content: {
    // Text spacing
    text: {
      lineGap: 4,
      paragraphGap: 16,
      headingGap: 24,
      listGap: 8,
    },

    // Media spacing
    media: {
      imageGap: 16,
      videoGap: 20,
      captionGap: 8,
    },

    // Code spacing
    code: {
      blockPadding: 16,
      inlinePadding: 4,
      lineGap: 2,
    },
  },
};

export type Spacing = typeof spacing;
